import React, { useState, useRef, useLayoutEffect } from "react";
import { Box, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTheme as useCustomTheme } from "../theme/useTheme";

/**
 * ImageCarousel
 *
 * Displays a list of images as thumbnails inside a fixed-width container.
 * When there are more images than fit, left/right chevron arrows appear
 * so the user can scroll through them.
 *
 * Props:
 *  - images: Array<{ id: string|number, url: string, isPending?: boolean }>
 *  - onRemove: (id) => void   — called when the × button is clicked
 *  - onImageClick: (url) => void — called when a thumbnail is clicked (preview)
 *  - readOnly: boolean — hides remove buttons
 *  - thumbnailSize: number (px) — default 110
 *  - gap: number (px) — default 8
 */
export function ImageCarousel({
  images = [],
  onRemove,
  onImageClick,
  readOnly = false,
  thumbnailSize = 110,
  gap = 8,
}) {
  const { neutrals } = useCustomTheme();

  const wrapperRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(1);

  const ARROW_SIZE = 24; // px por flecha

  // Recalculate how many thumbnails fit in the container
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const calc = () => {
      const totalWidth = el.clientWidth;
      const itemWidth = thumbnailSize + gap;

      // ¿Cuántas caben si NO hubiera flechas?
      const countWithoutArrows = Math.max(1, Math.floor(totalWidth / itemWidth));

      if (images.length <= countWithoutArrows) {
        // Caben todas → sin flechas, usa todo el ancho
        setVisibleCount(countWithoutArrows);
      } else {
        // No caben todas → necesitamos flechas, restamos su espacio
        const arrowsSpace = 2 * ARROW_SIZE + 2 * gap; // 2 flechas + 2 gaps
        const availableWithArrows = totalWidth - arrowsSpace;
        const countWithArrows = Math.max(1, Math.floor(availableWithArrows / itemWidth));
        setVisibleCount(countWithArrows);
      }
    };

    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [images.length, thumbnailSize, gap]);

  // Paginación: avanzamos de visibleCount en visibleCount para no repetir imágenes
  const totalPages = Math.ceil(images.length / visibleCount);
  const showArrows = totalPages > 1;

  // page va de 0 a totalPages-1
  const [page, setPage] = useState(0);
  const clampedPage = Math.min(page, totalPages - 1);
  const startIdx = clampedPage * visibleCount;
  const visibleImages = images.slice(startIdx, startIdx + visibleCount);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  // Reset page si se eliminan imágenes y la página actual ya no existe
  React.useEffect(() => {
    if (page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [totalPages, page]);

  const arrowBtnSx = {
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    padding: 0,
    flexShrink: 0,
    backgroundColor: neutrals.gray[100],
    border: `1px solid ${neutrals.gray[200]}`,
    borderRadius: "50%",
    "&:hover": { backgroundColor: neutrals.gray[200] },
  };

  return (
    <Box ref={wrapperRef} sx={{ display: "flex", alignItems: "center", gap: `${gap}px`, width: "100%" }}>
      {/* Left arrow */}
      {showArrows && (
        <IconButton
          onClick={handlePrev}
          disabled={clampedPage === 0}
          sx={{ ...arrowBtnSx, opacity: clampedPage === 0 ? 0.3 : 1 }}
        >
          <ChevronLeft size={14} />
        </IconButton>
      )}

      {/* Solo mostramos las imágenes que caben */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `${gap}px`,
            justifyContent: showArrows ? "center" : "flex-start",
          }}
        >
          {visibleImages.map((img) => (
            <Box
              key={img.id}
              sx={{
                position: "relative",
                flexShrink: 0,
                width: thumbnailSize,
                height: thumbnailSize,
                // Mostrar la cruz solo al hacer hover sobre el thumbnail
                "&:hover .remove-btn": {
                  opacity: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: `1px solid ${neutrals.gray[200]}`,
                  transition: "border-color 0.2s ease",
                  "&:hover": { borderColor: neutrals.gray[900] },
                }}
              >
                <Box
                  component="img"
                  src={img.url}
                  alt="Previsualización"
                  onClick={() => onImageClick?.(img.url)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    cursor: onImageClick ? "pointer" : "default",
                    display: "block",
                  }}
                />
              </Box>

              {!readOnly && (
                <IconButton
                  className="remove-btn"
                  onClick={() => onRemove?.(img.id)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    padding: 0,
                    opacity: 0,
                    transition: "opacity 0.15s ease, background-color 0.15s ease",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    border: "none",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <X size={13} color="#fff" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right arrow */}
      {showArrows && (
        <IconButton
          onClick={handleNext}
          disabled={clampedPage >= totalPages - 1}
          sx={{ ...arrowBtnSx, opacity: clampedPage >= totalPages - 1 ? 0.3 : 1 }}
        >
          <ChevronRight size={14} />
        </IconButton>
      )}
    </Box>
  );
}

